import { Routes, Route, Navigate, Outlet } from 'react-router-dom';
import { useAuth } from './hooks/auth';
import { useEffect, useState, Suspense, lazy } from 'react';
import { SkeletonScreen } from './components/Skeleton';
import { useStore } from './store';
import { ProtectedRoute, PublicOnlyRoute } from './routes';
import { VerifyEmailHandler } from './pages/VerifyEmailHandler';
import { Layout } from './components/Layout';
import { AuthSync } from './components/AuthSync';
import { useNavigationState } from './hooks/useNavigationState';

// Lazy loading страниц для code splitting
const LoginPage = lazy(() => import('./pages/LoginPage'));
const Dashboard = lazy(() => import('./pages/Dashboard'));
const ProfilePage = lazy(() => import('./pages/ProfilePage'));
const Help = lazy(() => import('./pages/Help'));
const EmailVerificationPendingPage = lazy(() => import('./pages/EmailVerificationPendingPage'));
const RoleSelectionPage = lazy(() => import('./pages/RoleSelectionPage'));
const AuthActionPage = lazy(() => import('./pages/AuthActionPage'));
const NotFound = lazy(() => import('./pages/NotFound'));
const LandingPage = lazy(() => import('./pages/LandingPage'));

// Check if we're in browser environment
const isBrowser = typeof window !== 'undefined';

// Компонент загрузки
function LoadingFallback() {
  console.log('[LoadingFallback] Rendering skeleton screen');
  return (
    <div className="min-h-screen bg-gradient-to-b from-surface to-background flex items-center justify-center p-4">
      <SkeletonScreen />
    </div>
  );
}

// Layout wrapper для protected routes
function ProtectedLayout() {
  const [currentTheme, setCurrentTheme] = useState<'light' | 'dark'>('dark');
  const [_showShortcutsHelp, setShowShortcutsHelp] = useState(false);

  useEffect(() => {
    // Initialize theme from localStorage or user settings (browser only)
    if (isBrowser) {
      const savedTheme = localStorage.getItem('theme') || 'dark';
      setCurrentTheme(savedTheme as 'light' | 'dark');
      document.documentElement.classList.toggle('dark', savedTheme === 'dark');
    }
  }, []);

  // Простая смена темы без эффектов
  const handleToggleTheme = () => {
    const newTheme = currentTheme === 'dark' ? 'light' : 'dark';
    setCurrentTheme(newTheme);
    
    // Update DOM and localStorage only in browser
    if (isBrowser) {
      localStorage.setItem('theme', newTheme);
      if (newTheme === 'dark') {
        document.documentElement.classList.add('dark');
      } else {
        document.documentElement.classList.remove('dark');
      }
    }
  };

  return (
    <Layout
      currentTheme={currentTheme}
      onToggleTheme={handleToggleTheme}
      onShowShortcutsHelp={() => setShowShortcutsHelp(true)}
    >
      <Outlet />
    </Layout>
  );
}

// VERSION: 1.0.3 - Layout refactor deployed
console.log('[App] VERSION: 1.0.3 - Layout refactor deployed at:', new Date().toISOString());

export default function App() {
  const { user, isAuthenticated, needsRoleSelection } = useAuth();

  // Initialize navigation state persistence - restores app/version/screen after page reload
  useNavigationState();

  // Initialize Firebase sync when user logs in
  useEffect(() => {
    if (isBrowser && isAuthenticated && user) {
      console.log('[App] User logged in, triggering Firebase sync...');
      console.log('[App] User details:', { id: user.id, email: user.email });
      // Pass userId explicitly to avoid race condition with AuthSync
      useStore.getState().syncWithFirebase(user.id);
    } else {
      console.log('[App] Sync NOT triggered:', { isBrowser, isAuthenticated: !!isAuthenticated, hasUser: !!user });
    }
  }, [isAuthenticated, user]);

  console.log('[App] ========== RENDER ==========');
  console.log('[App] User:', user?.email || 'null');
  console.log('[App] isAuthenticated:', isAuthenticated);
  console.log('[App] needsRoleSelection:', needsRoleSelection);
  console.log('[App] isEmailVerified:', user?.emailVerified);
  
  // Тестовый лог для диагностики - показываем текущий путь
  if (typeof window !== 'undefined') {
    console.log('[App] Current pathname:', window.location.pathname);
    console.log('[App] Current search:', window.location.search);
  }

  return (
    <>
      <AuthSync />
      <Suspense fallback={<LoadingFallback />}>
        <Routes>
          {/* Public routes - only for non-authenticated users */}
          <Route
            path="/login"
            element={
              <PublicOnlyRoute>
                <LoginPage />
              </PublicOnlyRoute>
            }
          />
          
          {/* Email verification - accessible for all (handles oobCode from email link) */}
          <Route
            path="/verify-email"
            element={<VerifyEmailHandler />}
          />
          
          {/* Email verification pending - shown after registration */}
          {/* Доступна только авторизованным пользователям (после регистрации) */}
          <Route
            path="/verify-email-pending"
            element={
              <ProtectedRoute requireEmailVerified={false}>
                <EmailVerificationPendingPage />
              </ProtectedRoute>
            }
          />
          
          {/* Auth action handler - for all Firebase action codes */}
          {/* ВАЖНО: Этот route НЕ обёрнут в ProtectedRoute */}
          {/* Firebase использует /auth/action (слеш), не /auth-action (дефис) */}
          <Route
            path="/auth/action"
            element={<AuthActionPage />}
          />
          
          {/* Role selection - for users with pending role */}
          {/* Требует только авторизацию, без проверки подтверждения email */}
          <Route
            path="/role-selection"
            element={
              <ProtectedRoute requireEmailVerified={false}>
                <RoleSelectionPage />
              </ProtectedRoute>
            }
          />
          
          {/* Landing page for non-authenticated, redirect to dashboard for authenticated */}
          {/* Если email не подтвержден - редирект на /verify-email-pending */}
          <Route
            path="/"
            element={
              !isAuthenticated ? (
                <LandingPage />
              ) : !user?.emailVerified ? (
                <Navigate to="/verify-email-pending" replace />
              ) : needsRoleSelection ? (
                <Navigate to="/role-selection" replace />
              ) : (
                <Navigate to="/dashboard" replace />
              )
            }
          />
          
          {/* Protected routes with Layout */}
          <Route element={<ProtectedRoute><ProtectedLayout /></ProtectedRoute>}>
            <Route path="/dashboard" element={<Dashboard />} />
            <Route path="/help" element={<Help />} />
            <Route path="/profile" element={<ProfilePage />} />
          </Route>
          
          {/* 404 */}
          <Route path="*" element={<NotFound />} />
        </Routes>
      </Suspense>
    </>
  );
}
